import type { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'
import type { DeviationSettingLevel } from 'api/digitalTwin/digitalTwin.types'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { CreateSettingData } from 'ui/components/DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'
import type { SettingEditorSelection } from './settingEditor.types'

/** Query parameters that open one property's settings, as the production plan's redirect sends them. */
const PROPERTY_QUERY_PARAMS = ['level', 'name', 'attribute'] as const

type SelectionState = {
  selection: SettingEditorSelection | null
  /** Changes on every selection, so an editor that stays open can tell a new one from a re-render. */
  revision: number
}

type SettingEditorSelectionState = SelectionState & {
  /** Without an attribute this opens the overview of all of the object's properties. */
  selectProperty: (target?: { level: DeviationSettingLevel; name: string; attribute?: string }) => void
  selectSetting: (setting: CreateSettingData) => void
  closeEditor: () => void
}

function findPropertyInQuery(search: string, properties: ConfigurableProperty[]): ConfigurableProperty | undefined {
  const params = new URLSearchParams(search)
  const [level, name, attribute] = PROPERTY_QUERY_PARAMS.map((param) => params.get(param))
  return properties.find(
    (property) => property.level === level && property.name === name && property.attribute === attribute
  )
}

function withoutPropertyQueryParams(search: string): string {
  const params = new URLSearchParams(search)
  PROPERTY_QUERY_PARAMS.forEach((param) => params.delete(param))
  return params.toString()
}

/**
 * The single setting editor the digital twin settings views have open; opening one replaces
 * whichever was open before. A property named in the URL is opened once, as soon as it is among
 * `configurableProperties`, and its parameters are then removed from the URL.
 */
export function useSettingEditorSelection(configurableProperties: ConfigurableProperty[]): SettingEditorSelectionState {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [{ selection, revision }, setState] = useState<SelectionState>({ selection: null, revision: 0 })

  const select = (next: SettingEditorSelection | null): void => {
    setState((previous) => ({ selection: next, revision: previous.revision + 1 }))
  }

  useEffect(() => {
    const property = findPropertyInQuery(search, configurableProperties)
    if (!property) {
      return
    }

    const { level, name, attribute } = property
    setState((previous) => ({
      selection: { kind: 'property', property: { level, name, attribute } },
      revision: previous.revision + 1,
    }))
    navigate({ pathname, search: withoutPropertyQueryParams(search) }, { replace: true })
  }, [configurableProperties, search, pathname, navigate])

  const selectProperty: SettingEditorSelectionState['selectProperty'] = (target) => {
    if (!target) {
      select(null)
    } else if (target.attribute === undefined) {
      select({ kind: 'object', level: target.level, name: target.name })
    } else {
      select({ kind: 'property', property: { level: target.level, name: target.name, attribute: target.attribute } })
    }
  }

  return {
    selection,
    revision,
    selectProperty,
    selectSetting: (setting) => select({ kind: 'setting', setting }),
    closeEditor: () => select(null),
  }
}
